import { useEffect, useRef } from "react"
import { ShareMessageFragment } from "~/__generated__/graphql"
import { FeedMessage } from "./message/feed-message"
import { AppText } from "~/ui/app-text"
import { cn } from "~/common/cn"

const COLLAPSED_HEADER_THRESHOLD = 40

interface MessageFeedProps {
  messages: ShareMessageFragment[]
  onNearTop?: (nearTop: boolean) => void
  onAtBottom?: () => void
  variant?: "mobile" | "desktop"
  contentPaddingTop?: number
  headerContent?: React.ReactNode
}

export const MessageFeed = ({
  messages,
  onNearTop,
  onAtBottom,
  variant = "mobile",
  contentPaddingTop,
  headerContent,
}: MessageFeedProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const hasHeader = !!headerContent
  const onNearTopRef = useRef(onNearTop)
  const onAtBottomRef = useRef(onAtBottom)
  useEffect(() => {
    onNearTopRef.current = onNearTop
    onAtBottomRef.current = onAtBottom
  })

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    let collapseTimer: ReturnType<typeof setTimeout> | null = null
    let lastExpanded = true

    const getState = () => {
      const maxScroll = el.scrollHeight - el.clientHeight
      const atTop = el.scrollTop < COLLAPSED_HEADER_THRESHOLD
      const nearBottom = el.scrollTop >= maxScroll - 40
      return { atTop, nearBottom, shouldExpand: atTop || nearBottom }
    }

    const handleScroll = () => {
      const { nearBottom, shouldExpand } = getState()

      if (variant !== "mobile") return

      if (shouldExpand && !lastExpanded) {
        if (collapseTimer) {
          clearTimeout(collapseTimer)
          collapseTimer = null
        }
        lastExpanded = true
        onNearTopRef.current?.(true)
        if (nearBottom) onAtBottomRef.current?.()
      } else if (!shouldExpand && lastExpanded) {
        if (!collapseTimer) {
          collapseTimer = setTimeout(() => {
            collapseTimer = null
            const rechecked = getState()
            if (!rechecked.shouldExpand) {
              lastExpanded = false
              onNearTopRef.current?.(false)
            }
          }, 200)
        }
      } else if (shouldExpand && lastExpanded) {
        if (collapseTimer) {
          clearTimeout(collapseTimer)
          collapseTimer = null
        }
        if (nearBottom) onAtBottomRef.current?.()
      }
    }

    const timerId = setTimeout(() => {
      el.addEventListener("scroll", handleScroll, { passive: true })
    }, 300)

    return () => {
      clearTimeout(timerId)
      if (collapseTimer) clearTimeout(collapseTimer)
      el.removeEventListener("scroll", handleScroll)
    }
  }, [variant, hasHeader])

  // Messages come in created_at_asc (oldest first) — no reversal needed.
  const messageList = messages.map((message, index) => {
    const prev = index > 0 ? messages[index - 1] : null
    const next = index < messages.length - 1 ? messages[index + 1] : null
    const isSameUserAsPrev = prev?.user?.id === message.user?.id
    const isSameUserAsNext = next?.user?.id === message.user?.id
    const prevHasReactions =
      (prev?.reactionGroups?.length ?? 0) > 0 || (prev?.childMessagesCount ?? 0) > 0

    return (
      <div
        key={message.id}
        className={
          isSameUserAsPrev ? (prevHasReactions ? "mt-1" : "mt-0.5") : index === 0 ? "" : "mt-2"
        }
      >
        <FeedMessage
          message={message}
          showName={!isSameUserAsPrev}
          showAvatar={!isSameUserAsNext}
          isFirstInGroup={!isSameUserAsPrev}
          isLastInGroup={!isSameUserAsNext}
        />
      </div>
    )
  })

  return (
    <div
      className={cn(
        "relative flex w-full flex-col",
        variant === "desktop" ? "h-full" : "flex-1 overflow-hidden"
      )}
    >
      <div
        ref={scrollRef}
        className={cn(
          "flex flex-col overflow-y-auto [-ms-overflow-style:none] [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          hasHeader ? "overscroll-none" : "overscroll-contain",
          variant === "desktop" ? "h-full bg-base-white px-1 pb-4 pt-14" : "flex-1"
        )}
      >
        {contentPaddingTop ? (
          <div className="shrink-0" style={{ height: contentPaddingTop }} />
        ) : null}
        {headerContent && <div className="shrink-0 bg-base-blue px-3 pb-4">{headerContent}</div>}
        <div
          className={cn("relative", variant === "desktop" ? "" : "bg-base-white pl-2 pr-4 pt-4")}
        >
          {messageList}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-base-white to-transparent pt-[80px]">
            <div
              className="flex flex-col items-center justify-end gap-1 pb-4 pt-14 backdrop-blur-md"
              style={{ maskImage: "linear-gradient(to bottom, transparent, black 40%)" }}
            >
              <AppText variant="h3" className="text-center uppercase">
                Join the group chat
              </AppText>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
